import { useEffect, useRef, useState } from 'react'
import { Grid, List } from 'semantic-ui-react'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { uniq } from 'lodash'
import './styles.scss'

import {
  Button,
  GridRowColumn,
  Icon,
  Text,
} from '@/componentsV1/BaseComponents'
import { TransactionCard } from '@/features/transactions/lists/TransactionCard'
import { TransactionHeader } from '@/features/transactions/lists/TransactionHeader'
import { AddTransactionCard } from '@/componentsV1/Transactions/AddTransactionCard'
import { useScrollRef } from '@/utils/sharedHooks'
import { TransactionCategoryIdentifier } from '@/reducers/admin/allTransactions.slice'
import { StepHeaders } from '@/features/bookkeeping/step-header'
import type { StepContext } from '@/features/bookkeeping/end-of-year-review/types'
import { Dialog, Button as UIButton } from '@/ui'

const filterCategories = [
  TransactionCategoryIdentifier.insurance_health,
  TransactionCategoryIdentifier.psychotherapy_fees,
  TransactionCategoryIdentifier.continuing_education,
  TransactionCategoryIdentifier.meals_entertainment,
  TransactionCategoryIdentifier.travel_expenses,
  TransactionCategoryIdentifier.rent_and_lease,
  TransactionCategoryIdentifier.internet,
  TransactionCategoryIdentifier.communication_expenses,
  TransactionCategoryIdentifier.auto_related_expenses,
  TransactionCategoryIdentifier.equipment,
  TransactionCategoryIdentifier.furniture,
  TransactionCategoryIdentifier.computer_hardware,
]

export const BusinessExpenseInPersonal = ({
  setIsStepComplete,
  stepContext,
  setStepContext,
}: {
  setIsStepComplete: (value: boolean) => void
  stepContext?: StepContext
  setStepContext?: (
    context: StepContext | ((prev: StepContext) => StepContext)
  ) => Promise<void>
}) => {
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false)
  const [addedTransactionIds, setAddedTransactionIds] = useState<number[]>([])
  const [editingTransactionId, setEditingTransactionId] = useState<number>()
  const { scrollRef, scrollToRef } = useScrollRef()

  const [hasBusinessExpenseToAdd, setHasBusinessExpenseToAdd] =
    useState<boolean>()
  const lastLoadedContextRef = useRef<string>('')
  const [showConfirmNoDialog, setShowConfirmNoDialog] = useState(false)

  // Load initial state from stepContext
  // Reload when stepContext changes and has meaningful data
  useEffect(() => {
    if (stepContext) {
      // Create a signature of the context to detect changes
      // Use null for undefined to ensure consistent stringification
      const contextSignature = JSON.stringify({
        hasBusinessExpenseToAdd: stepContext.hasBusinessExpenseToAdd ?? null,
        businessExpenseTransactionIds:
          stepContext.businessExpenseTransactionIds ?? null,
      })

      // Only load if the context has actually changed
      if (contextSignature !== lastLoadedContextRef.current) {
        // Load the value whether it's true, false, or undefined
        // This ensures false values are properly loaded
        setHasBusinessExpenseToAdd(stepContext.hasBusinessExpenseToAdd)
        // Always load transaction IDs if they exist in context
        if (stepContext.businessExpenseTransactionIds !== undefined) {
          setAddedTransactionIds(stepContext.businessExpenseTransactionIds)
        }
        lastLoadedContextRef.current = contextSignature
      }
    }
  }, [stepContext])

  useEffect(() => {
    if (hasBusinessExpenseToAdd !== undefined) {
      setIsStepComplete(true)
    } else {
      setIsStepComplete(false)
    }
  }, [hasBusinessExpenseToAdd, setIsStepComplete])

  useEffect(() => {
    if (showAddTransactionModal) {
      scrollToRef()
    }
  }, [scrollToRef, showAddTransactionModal])

  useEffect(() => {
    if (!hasBusinessExpenseToAdd) {
      setShowAddTransactionModal(false)
    }
  }, [hasBusinessExpenseToAdd])

  useEffect(() => {
    if (!showAddTransactionModal) {
      setEditingTransactionId(undefined)
    }
  }, [showAddTransactionModal])

  const addTransactionToList = (transactionId: number) => {
    const newIds = uniq([...addedTransactionIds, transactionId])
    setAddedTransactionIds(newIds)
    // Immediately save to context
    if (setStepContext) {
      setStepContext((prev) => ({
        ...prev,
        businessExpenseTransactionIds: newIds,
      }))
    }
  }

  const editTransaction = (transactionId: number) => {
    setShowAddTransactionModal(true)
    setEditingTransactionId(transactionId)
  }

  const deleteTransaction = (transactionId: number) => {
    const newIds = addedTransactionIds.filter((tId) => tId !== transactionId)
    setAddedTransactionIds(newIds)
    // Immediately save to context
    if (setStepContext) {
      setStepContext((prev) => ({
        ...prev,
        businessExpenseTransactionIds: newIds,
      }))
    }
  }

  return (
    <Grid>
      <GridRowColumn centerContent>
        <StepHeaders
          imageSrc="https://heard-images.s3.amazonaws.com/assets/credit_card.svg"
          imageAlt="creditcard"
          imageWidth={180}
          imageHeight={180}
          title="Do you have business expenses from these categories in your personal accounts?"
          customColor="yellow"
        >
          <Grid>
            <Grid.Row className="list-column">
              <Grid.Column width={8}>
                <List bulleted className="flex-column">
                  <List.Item>Therapy</List.Item>
                  <List.Item>Continuing Education</List.Item>
                  <List.Item className="align-left">
                    Meals, Lodging and Airfare for Business Travel
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column width={8}>
                <List bulleted className="flex-column">
                  <List.Item className="align-left">
                    Equipment or Furniture over $2,500
                  </List.Item>
                </List>
              </Grid.Column>
            </Grid.Row>
            <GridRowColumn className="align-left">
              <Text>
                We ask this to ensure that we account for any expenses that you
                may have accidentally made using a personal account.
              </Text>
            </GridRowColumn>
          </Grid>
        </StepHeaders>
      </GridRowColumn>
      <Grid.Row className="buttons-grid">
        <Grid.Column width={6} />
        <Grid.Column width={2}>
          <Button
            onClick={() => {
              setHasBusinessExpenseToAdd(true)
              if (setStepContext) {
                setStepContext((prev) => ({
                  ...prev,
                  hasBusinessExpenseToAdd: true,
                }))
              }
            }}
            variant="selector"
            fullWidth
            active={hasBusinessExpenseToAdd === true}
          >
            Yes
          </Button>
        </Grid.Column>
        <Grid.Column width={2}>
          <Button
            onClick={() => {
              // Show confirmation if there are transactions
              if (addedTransactionIds.length > 0) {
                setShowConfirmNoDialog(true)
              } else {
                // No transactions, proceed directly
                setHasBusinessExpenseToAdd(false)
                if (setStepContext) {
                  setStepContext((prev) => ({
                    ...prev,
                    hasBusinessExpenseToAdd: false,
                    businessExpenseTransactionIds: [],
                  }))
                }
              }
            }}
            variant="selector"
            fullWidth
            active={hasBusinessExpenseToAdd === false}
          >
            No
          </Button>
        </Grid.Column>
      </Grid.Row>
      {hasBusinessExpenseToAdd && (
        <>
          <GridRowColumn>
            <Text textAlign="center">
              Please add transactions that haven’t already been recorded in
              Heard.
            </Text>
          </GridRowColumn>
          <GridRowColumn spacer={6} width={4}>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setShowAddTransactionModal(true)}
            >
              <Icon icon={faPlus} style={{ marginRight: 10 }} />
              Add Transaction
            </Button>
          </GridRowColumn>
          <Grid.Row />
          {showAddTransactionModal && (
            <>
              <GridRowColumn spacer={2} width={12}>
                <span ref={scrollRef} />
                <AddTransactionCard
                  onClose={() => setShowAddTransactionModal(false)}
                  onSave={addTransactionToList}
                  transactionId={editingTransactionId}
                  categoryType="Expenses"
                  categoryFilter={filterCategories}
                />
              </GridRowColumn>
              <Grid.Row />
            </>
          )}

          {addedTransactionIds.length > 0 && <TransactionHeader />}
          <GridRowColumn short className="transaction-grid">
            {addedTransactionIds.map((id) => (
              <TransactionCard
                key={id}
                transactionId={id}
                variant="edit"
                editAction={editTransaction}
                deleteAction={deleteTransaction}
              />
            ))}
          </GridRowColumn>
        </>
      )}

      <Dialog
        open={showConfirmNoDialog}
        onClose={() => setShowConfirmNoDialog(false)}
      >
        <Dialog.Content size="sm">
          <Dialog.Title>
            Please review and delete transactions first
          </Dialog.Title>
          <Dialog.Description className="mt-2">
            You have {addedTransactionIds.length} transaction
            {addedTransactionIds.length !== 1 ? 's' : ''} added. Please delete
            all transactions before selecting &quot;No&quot;. You can delete
            transactions individually using the delete button on each
            transaction card.
          </Dialog.Description>
          <Dialog.Footer className="mt-6">
            <Dialog.Close asChild>
              <UIButton variant="secondary">Got it</UIButton>
            </Dialog.Close>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </Grid>
  )
}
